import type {
  AthenaConditionArrayValue,
  AthenaConditionCastType,
  AthenaConditionOperator,
  AthenaConditionValue,
  AthenaDeletePayload,
  AthenaGatewayCallOptions,
  AthenaGatewayConnectionOptions,
  AthenaGatewayConnectionResult,
  AthenaGatewayCondition,
  AthenaGatewayErrorDetails,
  AthenaInsertPayload,
  AthenaJsonObject,
  AthenaJsonValue,
  AthenaRpcCallOptions,
  AthenaRpcFilter,
  AthenaRpcFilterOperator,
  AthenaRpcPayload,
  AthenaSortBy,
  AthenaUpdatePayload,
} from './gateway/types.ts'
import type { BackendConfig } from './gateway/types.ts'
import {
  createResultFormatter,
  executeRead,
  toSingleResult,
} from './client-result.ts'
import type {
  AthenaResult,
  AthenaResultError,
  AthenaResultFormatter,
} from './client-result.ts'
import {
  buildDebugSelectQuery,
  buildDeleteDebugSql,
  buildInsertDebugSql,
  buildRpcDebugSql,
  buildTypedSelectQuery,
  buildUpdateDebugSql,
  resolveTableNameForCall,
} from './client-sql.ts'
export type { AthenaResult, AthenaResultError } from './client-result.ts'
import { createAthenaRequest } from './client-request.ts'
import type {
  AthenaRequestOptions,
  AthenaRequestResponse,
} from './client-request.ts'
export type {
  AthenaRequestMethod,
  AthenaRequestOptions,
  AthenaRequestQueryValueMap,
  AthenaRequestResponse,
  AthenaRequestService,
} from './client-request.ts'
import { normalizeAthenaGatewayBaseUrl } from './gateway/url.ts'
import {
  createAthenaGatewayClient,
  createAthenaGatewayClientView,
  type AthenaGatewayClient,
} from './gateway/client.ts'
import { createAuthModule } from './auth/client.ts'
import type { AthenaAuthBindings, AthenaAuthClientConfig } from './auth/types.ts'
import type { AthenaDbModule } from './db/module.ts'
import { createStorageModule } from './storage/module.ts'
import type { AthenaStorageClientConfig, AthenaStorageModule } from './storage/module.ts'
import { createChatModule } from './chat/module.ts'
import type { AthenaChatModule, AthenaChatWebSocketFactory } from './chat/types.ts'
import {
  compileOrderBy,
  compileSelectShape,
  compileWhere,
  selectShapeUsesRelationSchema,
  shouldUseUuidTextComparison,
} from './query-ast.ts'
import type {
  AthenaFindManyOptions,
  AthenaFindManyResult,
  AthenaSelectShape,
  AthenaValidatedSelectShape,
} from './query-ast.ts'
import {
  canUseFindManyAstTransport,
  createSelectTransportPlan,
  findManyAstWhereRequiresLegacyTransport,
  normalizeFindManyAstWhere,
  toFindManyAstOrder,
} from './query-transport.ts'
import type { AthenaFindManyAstPayload } from './query-transport.ts'
import {
  buildDeleteDebugAst,
  buildFindManyCompiledDebugAst,
  buildFindManyDirectDebugAst,
  buildInsertDebugAst,
  buildRawQueryDebugAst,
  buildRpcDebugAst,
  buildSelectDebugAst,
  buildUpdateDebugAst,
  buildUpsertDebugAst,
} from './query-debug-ast.ts'
import type { AthenaQueryDebugAst } from './query-debug-ast.ts'
import {
  captureTraceCallsite,
  createQueryTracer,
  createTraceCallsiteStore,
  executeWithQueryTrace,
} from './query-tracing.ts'
import type {
  AthenaQueryTracer,
} from './query-tracing.ts'
import {
  isAthenaModelTarget,
  resolveAthenaModelTargetTableName,
} from './schema/model-target.ts'
import type {
  AthenaSelectInput,
  AthenaTypecheckedColumnKey,
  AthenaValidatedSelectInput,
} from './select-column-types.ts'

export type {
  AthenaColumnKey,
  AthenaColumnKeyWithAutocomplete,
  AthenaResolvedColumnKey,
  AthenaSelectArrayElement,
  AthenaSelectColumnsFor,
  AthenaSelectInput,
  AthenaSelectInputHints,
  AthenaTypecheckedColumnKey,
  AthenaValidatedSelectInput,
  HasKnownSelectColumns,
} from './select-column-types.ts'
import type {
  AthenaClientModelsInput,
  AthenaClientModelForTableName,
  AthenaClientTableName,
  AthenaModelTarget,
  InsertOf,
  RowOf,
  UpdateOf,
} from './schema/types.ts'

export interface InternalClientBehaviorOptions {
  /**
   * Retry retryable read failures (`select`, `findMany`, `query`) with a fixed internal policy.
   *
   * Applies two additional attempts with exponential backoff and jitter.
   */
  retryReads?: boolean
  /**
   * Emit execution diagnostics for every query/mutation/RPC invocation.
   * Includes payload, synthesized SQL, full outcome, and best-effort callsite metadata.
   */
  traceQueries?: boolean | AthenaQueryTraceOptions
  /**
   * Build and attach a normalized operation AST for runtime debugging.
   *
   * When enabled, successful Athena results expose a non-enumerable debug AST
   * that can be read with `getAthenaDebugAst(...)`. If tracing is also enabled,
   * the same AST is included on emitted trace events.
   */
  debugAst?: boolean
  /**
   * Send the original `findMany(...)` AST body for clean object-select reads.
   * This requires gateway support and falls back to legacy compiled transport
   * when a chain carries filter/pagination state that the AST payload cannot
   * represent losslessly yet.
   */
  findManyAst?: boolean
}

export interface AthenaQueryTraceOptions {
  /**
   * Custom sink for trace events. Defaults to console.info.
   */
  logger?: (event: AthenaQueryTraceEvent) => void
}

export interface AthenaQueryTraceCallsite {
  filePath: string
  fileName: string
  line: number
  column: number
  frame?: string
  functionName?: string
}

export interface AthenaQueryTraceEvent {
  timestamp: string
  durationMs: number
  operation: 'select' | 'insert' | 'upsert' | 'update' | 'delete' | 'rpc' | 'query'
  endpoint: '/gateway/fetch' | '/gateway/insert' | '/gateway/update' | '/gateway/delete' | '/gateway/rpc' | '/gateway/query' | `/rpc/${string}`
  table?: string
  functionName?: string
  sql: string
  payload: unknown
  ast?: AthenaQueryDebugAst
  options?: AthenaGatewayCallOptions | AthenaRpcCallOptions
  callsite: AthenaQueryTraceCallsite | null
  outcome?: {
    status: number
    error: AthenaResultError | null
    errorDetails?: AthenaGatewayErrorDetails | null
    count?: number | null
    data: unknown
    raw: unknown
  }
  thrownError?: unknown
}

type TableBuilderState = {
  conditions: AthenaGatewayCondition[]
  limit?: number
  offset?: number
  order?: AthenaSortBy
  currentPage?: number
  pageSize?: number
  totalPages?: number
}

type ConditionCastHints = {
  valueCast?: AthenaConditionCastType
  columnCast?: AthenaConditionCastType
}

type MutationSingleResult<Result> = Result extends Array<infer Item> ? Item | null : Result | null
type MutationResultRow<Result> = Result extends Array<infer Item> ? Item : Result
type AthenaRowShape = Record<string, AthenaJsonValue | undefined>
type FilterColumnKey<Row> = Extract<keyof NonNullable<Row>, string>
/** Known keys when the row shape is concrete; `string` when untyped / index signature. */
type ResolvedFilterColumnKey<Row> = [FilterColumnKey<Row>] extends [never]
  ? string
  : string extends FilterColumnKey<Row>
    ? string
    : FilterColumnKey<Row>
/**
 * Table-name argument for free-form `from<Row>(table)`.
 * - No client `models` → `string` (nothing to complete).
 * - With `models` → known bare/qualified names (IntelliSense + reject unknowns).
 *   Prefer `.from("users")` without a row generic so the row type is inferred from the model.
 */
type UntypedTableName<TModels> = [TModels] extends [never]
  ? string
  : [AthenaClientTableName<TModels>] extends [never]
    ? string
    : AthenaClientTableName<TModels>
type ResolvedClientTableModel<TModels, TTableName extends string> = Extract<
  AthenaClientModelForTableName<TModels, TTableName>,
  AthenaModelTarget
>
type ClientTableQueryBuilder<
  TModels,
  TTableName extends string,
> = TableQueryBuilder<
  RowOf<ResolvedClientTableModel<TModels, TTableName>>,
  InsertOf<ResolvedClientTableModel<TModels, TTableName>>,
  UpdateOf<ResolvedClientTableModel<TModels, TTableName>>
>
/**
 * Select/returning/single column input.
 *
 * - Typed row: validates string lists; arrays use the model-key union.
 * - Untyped row: plain `TValue` (`string | string[]`).
 */
type SelectColumnsFor<
  Row,
  TValue extends AthenaSelectInput,
> = AthenaValidatedSelectInput<Row, TValue>
const DEFAULT_COLUMNS = '*'

type SelectDebugAstFactory = (input: {
  tableName: string
  columns: string | string[]
  executionState: TableBuilderState
  plan: ReturnType<typeof createSelectTransportPlan>
}) => AthenaQueryDebugAst

export interface MutationQuery<
  Result,
  Row = MutationResultRow<Result>,
> extends PromiseLike<AthenaResult<Result>> {
  select<const TColumns extends AthenaSelectInput = string>(
    columns?: SelectColumnsFor<Row, TColumns>,
    options?: AthenaGatewayCallOptions,
  ): Promise<AthenaResult<Result>>
  returning<const TColumns extends AthenaSelectInput = string>(
    columns?: SelectColumnsFor<Row, TColumns>,
    options?: AthenaGatewayCallOptions,
  ): Promise<AthenaResult<Result>>
  single<const TColumns extends AthenaSelectInput = string>(
    columns?: SelectColumnsFor<Row, TColumns>,
    options?: AthenaGatewayCallOptions,
  ): Promise<AthenaResult<MutationSingleResult<Result>>>
  maybeSingle<const TColumns extends AthenaSelectInput = string>(
    columns?: SelectColumnsFor<Row, TColumns>,
    options?: AthenaGatewayCallOptions,
  ): Promise<AthenaResult<MutationSingleResult<Result>>>
  then<TResult1 = AthenaResult<Result>, TResult2 = never>(
    onfulfilled?: ((value: AthenaResult<Result>) => TResult1 | PromiseLike<TResult1>) | undefined | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | undefined | null,
  ): Promise<TResult1 | TResult2>
  catch<TResult = never>(
    onrejected?: ((reason: unknown) => TResult | PromiseLike<TResult>) | undefined | null,
  ): Promise<AthenaResult<Result> | TResult>
  finally(onfinally?: (() => void) | undefined | null): Promise<AthenaResult<Result>>
}

function mergeOptions<T extends { headers?: Record<string, string> }>(
  ...options: Array<T | undefined>
): T | undefined {
  return options.reduce<T | undefined>((acc, next) => {
    if (!next) return acc
    const merged = { ...(acc ?? {}), ...next } as T
    if (acc?.headers || next.headers) {
      merged.headers = {
        ...(acc?.headers ?? {}),
        ...(next.headers ?? {}),
      }
    }
    return merged
  }, undefined)
}

function asAthenaJsonObject(value: unknown): AthenaJsonObject {
  return value as unknown as AthenaJsonObject
}

function asAthenaJsonObjectArray(values: unknown[]): AthenaJsonObject[] {
  return values as unknown as AthenaJsonObject[]
}

function normalizeSelectColumnsInput(columns?: AthenaSelectInput): string | string[] | undefined {
  if (columns === undefined) {
    return undefined
  }
  if (typeof columns === 'string') {
    return columns
  }
  return [...columns]
}

function createMutationQuery<
  Result,
  Row = MutationResultRow<Result>,
>(
  executor: (
    columns?: string | string[],
    options?: AthenaGatewayCallOptions,
    callsite?: AthenaQueryTraceCallsite | null,
  ) => Promise<AthenaResult<Result>>,
  defaultColumns: AthenaSelectInput | null = DEFAULT_COLUMNS,
  tracer?: AthenaQueryTracer,
  initialCallsite?: AthenaQueryTraceCallsite | null,
): MutationQuery<Result, Row> {
  let selectedColumns: AthenaSelectInput | undefined = defaultColumns === null ? undefined : defaultColumns
  let selectedOptions: AthenaGatewayCallOptions | undefined
  let promise: Promise<AthenaResult<Result>> | null = null
  const callsiteStore = createTraceCallsiteStore(tracer, initialCallsite)

  const run = (
    columns?: AthenaSelectInput,
    options?: AthenaGatewayCallOptions,
    callsite?: AthenaQueryTraceCallsite | null,
  ) => {
    const payloadColumns = columns ?? selectedColumns
    const payloadOptions = options ?? selectedOptions
    if (!promise) {
      promise = executor(
        normalizeSelectColumnsInput(payloadColumns),
        payloadOptions,
        callsiteStore.resolve(callsite),
      )
    }
    return promise
  }

  const mutationQuery: MutationQuery<Result, Row> = {
    select(columns?: AthenaSelectInput, options?: AthenaGatewayCallOptions) {
      selectedColumns = columns
      selectedOptions = options ?? selectedOptions
      return run(columns, options, captureTraceCallsite(tracer))
    },
    returning(columns?: AthenaSelectInput, options?: AthenaGatewayCallOptions) {
      return mutationQuery.select(columns, options)
    },
    single(columns?: AthenaSelectInput, options?: AthenaGatewayCallOptions) {
      selectedColumns = columns
      selectedOptions = options ?? selectedOptions
      return run(columns, options, captureTraceCallsite(tracer)).then(toSingleResult)
    },
    maybeSingle(columns?: AthenaSelectInput, options?: AthenaGatewayCallOptions) {
      return mutationQuery.single(columns, options)
    },
    then(onfulfilled, onrejected) {
      return run(selectedColumns, selectedOptions).then(onfulfilled, onrejected)
    },
    catch(onrejected) {
      return run(selectedColumns, selectedOptions).catch(onrejected)
    },
    finally(onfinally) {
      return run(selectedColumns, selectedOptions).finally(onfinally)
    },
  }

  return mutationQuery
}

export interface OrderOptions {
  ascending?: boolean
}

/** Shared filter chain - supports eq, limit, etc. in any order relative to select/update */
interface FilterChain<Self, Row> {
  eq(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue): Self
  eqCast(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue, cast: AthenaConditionCastType): Self
  eqUuid(column: ResolvedFilterColumnKey<Row>, value: string): Self
  match(filters: Partial<Record<ResolvedFilterColumnKey<Row>, AthenaConditionValue>>): Self
  range(from: number, to: number): Self
  limit(count: number): Self
  offset(count: number): Self
  currentPage(value: number): Self
  pageSize(value: number): Self
  totalPages(value: number): Self
  order(column: ResolvedFilterColumnKey<Row>, options?: OrderOptions): Self
  gt(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue): Self
  gte(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue): Self
  lt(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue): Self
  lte(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue): Self
  neq(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue): Self
  like(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue): Self
  ilike(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue): Self
  is(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue): Self
  in(column: ResolvedFilterColumnKey<Row>, values: AthenaConditionArrayValue): Self
  contains(column: ResolvedFilterColumnKey<Row>, values: AthenaConditionArrayValue): Self
  containedBy(column: ResolvedFilterColumnKey<Row>, values: AthenaConditionArrayValue): Self
  not(
    columnOrExpression: ResolvedFilterColumnKey<Row> | string,
    operator?: AthenaConditionOperator,
    value?: AthenaConditionValue,
  ): Self
  or(expression: string): Self
}

/** Chain returned by select() - supports filters and single/maybeSingle before execution */
export interface SelectChain<Row, SelectedRow = Row>
  extends FilterChain<SelectChain<Row, SelectedRow>, Row>, PromiseLike<AthenaResult<SelectedRow[]>> {
  single<
    T = SelectedRow,
    const TColumns extends AthenaSelectInput = string,
  >(
    columns?: SelectColumnsFor<Row, TColumns>,
    options?: AthenaGatewayCallOptions,
  ): Promise<AthenaResult<T | null>>
  maybeSingle<
    T = SelectedRow,
    const TColumns extends AthenaSelectInput = string,
  >(
    columns?: SelectColumnsFor<Row, TColumns>,
    options?: AthenaGatewayCallOptions,
  ): Promise<AthenaResult<T | null>>
}

/** Chain returned by update() - supports filters before execution, plus select/returning */
export interface UpdateChain<Row>
  extends FilterChain<UpdateChain<Row>, Row>, MutationQuery<Row[], Row> {}

interface RpcFilterChain<Self, Row> {
  eq(column: AthenaTypecheckedColumnKey<Row>, value: AthenaConditionValue): Self
  neq(column: AthenaTypecheckedColumnKey<Row>, value: AthenaConditionValue): Self
  gt(column: AthenaTypecheckedColumnKey<Row>, value: AthenaConditionValue): Self
  gte(column: AthenaTypecheckedColumnKey<Row>, value: AthenaConditionValue): Self
  lt(column: AthenaTypecheckedColumnKey<Row>, value: AthenaConditionValue): Self
  lte(column: AthenaTypecheckedColumnKey<Row>, value: AthenaConditionValue): Self
  like(column: AthenaTypecheckedColumnKey<Row>, value: AthenaConditionValue): Self
  ilike(column: AthenaTypecheckedColumnKey<Row>, value: AthenaConditionValue): Self
  is(column: AthenaTypecheckedColumnKey<Row>, value: AthenaConditionValue): Self
  in(column: AthenaTypecheckedColumnKey<Row>, values: AthenaConditionArrayValue): Self
}

export interface RpcOrderOptions {
  ascending?: boolean
}

export interface RpcQueryBuilder<Row>
  extends RpcFilterChain<RpcQueryBuilder<Row>, Row>, PromiseLike<AthenaResult<Row[]>> {
  select<const TColumns extends AthenaSelectInput = string>(
    columns?: SelectColumnsFor<Row, TColumns>,
    options?: AthenaRpcCallOptions,
  ): Promise<AthenaResult<Row[]>>
  single<
    T = Row,
    const TColumns extends AthenaSelectInput = string,
  >(
    columns?: SelectColumnsFor<Row, TColumns>,
    options?: AthenaRpcCallOptions,
  ): Promise<AthenaResult<T | null>>
  maybeSingle<
    T = Row,
    const TColumns extends AthenaSelectInput = string,
  >(
    columns?: SelectColumnsFor<Row, TColumns>,
    options?: AthenaRpcCallOptions,
  ): Promise<AthenaResult<T | null>>
  order(column: AthenaTypecheckedColumnKey<Row>, options?: RpcOrderOptions): RpcQueryBuilder<Row>
  limit(count: number): RpcQueryBuilder<Row>
  offset(count: number): RpcQueryBuilder<Row>
  range(from: number, to: number): RpcQueryBuilder<Row>
}

export interface AthenaFromOptions {
  schema?: string
}

export interface TableQueryBuilder<
  Row,
  Insert = Partial<Row>,
  Update = Partial<Insert>,
  TContext = unknown,
> extends FilterChain<TableQueryBuilder<Row, Insert, Update, TContext>, Row> {
  select<
    T = Row,
    const TColumns extends AthenaSelectInput = string,
  >(
    columns?: SelectColumnsFor<Row, TColumns>,
    options?: AthenaGatewayCallOptions,
  ): SelectChain<Row, T>
  findMany<const TSelect extends AthenaSelectShape>(
    options: AthenaFindManyOptions<Row, TSelect> & {
      select: AthenaValidatedSelectShape<Row, TSelect>
    },
  ): Promise<AthenaResult<Array<AthenaFindManyResult<Row, TSelect, TContext>>>>
  insert(values: Insert, options?: AthenaGatewayCallOptions): MutationQuery<Row, Row>
  insert(values: Insert[], options?: AthenaGatewayCallOptions): MutationQuery<Row[], Row>
  upsert(
    values: Insert,
    options?: AthenaGatewayCallOptions & {
      updateBody?: Update
      onConflict?: ResolvedFilterColumnKey<Row> | Array<ResolvedFilterColumnKey<Row>> | (string & {}) | Array<string & {}>
    },
  ): MutationQuery<Row, Row>
  upsert(
    values: Insert[],
    options?: AthenaGatewayCallOptions & {
      updateBody?: Update
      onConflict?: ResolvedFilterColumnKey<Row> | Array<ResolvedFilterColumnKey<Row>> | (string & {}) | Array<string & {}>
    },
  ): MutationQuery<Row[], Row>
  update(values: Update, options?: AthenaGatewayCallOptions): UpdateChain<Row>
  delete(options?: AthenaGatewayCallOptions & { resourceId?: string }): MutationQuery<Row | null, Row>
  single<
    T = Row,
    const TColumns extends AthenaSelectInput = string,
  >(
    columns?: SelectColumnsFor<Row, TColumns>,
    options?: AthenaGatewayCallOptions,
  ): Promise<AthenaResult<T | null>>
  maybeSingle<
    T = Row,
    const TColumns extends AthenaSelectInput = string,
  >(
    columns?: SelectColumnsFor<Row, TColumns>,
    options?: AthenaGatewayCallOptions,
  ): Promise<AthenaResult<T | null>>
  reset(): TableQueryBuilder<Row, Insert, Update, TContext>
}

function getResourceId(state: TableBuilderState): string | undefined {
  const candidate = state.conditions.find(
    condition =>
      condition.operator === 'eq' &&
      (condition.column === 'resource_id' || condition.column === 'id'),
  )
  return candidate?.value?.toString()
}

function stringifyFilterValue(value: AthenaConditionValue | AthenaConditionArrayValue | string): string {
  if (Array.isArray(value)) {
    return value.join(',')
  }
  return String(value)
}

function createFilterMethods<Self, Row>(
  state: TableBuilderState,
  addCondition: (
    operator: AthenaConditionOperator,
    column?: string,
    value?: AthenaConditionValue | AthenaConditionArrayValue | string,
    hints?: ConditionCastHints,
  ) => void,
  self: Self,
): FilterChain<Self, Row> {
  return {
    eq(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue) {
      const columnName = String(column)
      if (shouldUseUuidTextComparison(columnName, value)) {
        addCondition('eq', columnName, value, { columnCast: 'text' })
      } else {
        addCondition('eq', columnName, value)
      }
      return self
    },
    eqCast(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue, cast: AthenaConditionCastType) {
      addCondition('eq', String(column), value, { valueCast: cast })
      return self
    },
    eqUuid(column: ResolvedFilterColumnKey<Row>, value: string) {
      addCondition('eq', String(column), value, { valueCast: 'uuid' })
      return self
    },
    match(filters: Partial<Record<ResolvedFilterColumnKey<Row>, AthenaConditionValue>>) {
      Object.entries(filters as Record<string, AthenaConditionValue | undefined>).forEach(([column, value]) => {
        if (value === undefined) {
          return
        }
        if (shouldUseUuidTextComparison(column, value)) {
          addCondition('eq', column, value, { columnCast: 'text' })
        } else {
          addCondition('eq', column, value)
        }
      })
      return self
    },
    range(from: number, to: number) {
      state.offset = from
      state.limit = to - from + 1
      return self
    },
    limit(count: number) {
      state.limit = count
      return self
    },
    offset(count: number) {
      state.offset = count
      return self
    },
    currentPage(value: number) {
      state.currentPage = value
      return self
    },
    pageSize(value: number) {
      state.pageSize = value
      return self
    },
    totalPages(value: number) {
      state.totalPages = value
      return self
    },
    order(column: ResolvedFilterColumnKey<Row>, options?: OrderOptions) {
      state.order = {
        field: String(column),
        direction: options?.ascending === false ? 'descending' : 'ascending',
      }
      return self
    },
    gt(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue) {
      addCondition('gt', String(column), value)
      return self
    },
    gte(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue) {
      addCondition('gte', String(column), value)
      return self
    },
    lt(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue) {
      addCondition('lt', String(column), value)
      return self
    },
    lte(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue) {
      addCondition('lte', String(column), value)
      return self
    },
    neq(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue) {
      addCondition('neq', String(column), value)
      return self
    },
    like(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue) {
      addCondition('like', String(column), value)
      return self
    },
    ilike(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue) {
      addCondition('ilike', String(column), value)
      return self
    },
    is(column: ResolvedFilterColumnKey<Row>, value: AthenaConditionValue) {
      addCondition('is', String(column), value)
      return self
    },
    in(column: ResolvedFilterColumnKey<Row>, values: AthenaConditionArrayValue) {
      addCondition('in', String(column), values)
      return self
    },
    contains(column: ResolvedFilterColumnKey<Row>, values: AthenaConditionArrayValue) {
      addCondition('contains', String(column), values)
      return self
    },
    containedBy(column: ResolvedFilterColumnKey<Row>, values: AthenaConditionArrayValue) {
      addCondition('containedBy', String(column), values)
      return self
    },
    not(
      columnOrExpression: ResolvedFilterColumnKey<Row> | string,
      operator?: AthenaConditionOperator,
      value?: AthenaConditionValue,
    ) {
      const expression = String(columnOrExpression)
      if (operator != null && value !== undefined) {
        addCondition('not', undefined, `${expression}.${operator}.${stringifyFilterValue(value)}`)
      } else {
        addCondition('not', undefined, expression)
      }
      return self
    },
    or(expression: string) {
      addCondition('or', undefined, expression)
      return self
    },
  }
}

function toRpcSelect(columns?: AthenaSelectInput) {
  if (!columns) return undefined
  if (typeof columns === 'string') {
    return columns
  }
  return columns.join(',')
}

function createRpcFilterMethods<Self>(
  filters: AthenaRpcFilter[],
  self: Self,
) {
  const addFilter = (
    operator: AthenaRpcFilterOperator,
    column: string,
    value: AthenaConditionValue | AthenaConditionArrayValue | string,
  ) => {
    filters.push({ column, operator, value })
  }

  return {
    eq(column: string, value: AthenaConditionValue) {
      addFilter('eq', column, value)
      return self
    },
    neq(column: string, value: AthenaConditionValue) {
      addFilter('neq', column, value)
      return self
    },
    gt(column: string, value: AthenaConditionValue) {
      addFilter('gt', column, value)
      return self
    },
    gte(column: string, value: AthenaConditionValue) {
      addFilter('gte', column, value)
      return self
    },
    lt(column: string, value: AthenaConditionValue) {
      addFilter('lt', column, value)
      return self
    },
    lte(column: string, value: AthenaConditionValue) {
      addFilter('lte', column, value)
      return self
    },
    like(column: string, value: AthenaConditionValue) {
      addFilter('like', column, value)
      return self
    },
    ilike(column: string, value: AthenaConditionValue) {
      addFilter('ilike', column, value)
      return self
    },
    is(column: string, value: AthenaConditionValue) {
      addFilter('is', column, value)
      return self
    },
    in(column: string, values: AthenaConditionArrayValue) {
      addFilter('in', column, values)
      return self
    },
  }
}

function createRpcBuilder<Row>(
  functionName: string,
  args: AthenaJsonObject | undefined,
  baseOptions: AthenaRpcCallOptions | undefined,
  client: ReturnType<typeof createAthenaGatewayClient>,
  formatGatewayResult: AthenaResultFormatter,
  tracer?: AthenaQueryTracer,
  initialCallsite?: AthenaQueryTraceCallsite | null,
  debugAstEnabled = false,
): RpcQueryBuilder<Row> {
  const state: {
    filters: AthenaRpcFilter[]
    limit?: number
    offset?: number
    order?: { column: string; ascending?: boolean }
  } = {
    filters: [],
  }

  let selectedColumns: AthenaSelectInput | undefined
  let selectedOptions: AthenaRpcCallOptions | undefined
  let promise: Promise<AthenaResult<Row[]>> | null = null
  const callsiteStore = createTraceCallsiteStore(tracer, initialCallsite)

  const executeRpc = async <SelectedRow = Row>(
    columns?: AthenaSelectInput,
    options?: AthenaRpcCallOptions,
    callsite?: AthenaQueryTraceCallsite | null,
  ): Promise<AthenaResult<SelectedRow[]>> => {
    const mergedOptions = mergeOptions(baseOptions, options)
    const normalizedSelectedColumns = normalizeSelectColumnsInput(columns)
    const payload: AthenaRpcPayload = {
      function: functionName,
      args,
      schema: mergedOptions?.schema,
      select: toRpcSelect(columns),
      filters: state.filters.length ? [...state.filters] : undefined,
      count: mergedOptions?.count,
      head: mergedOptions?.head,
      limit: state.limit,
      offset: state.offset,
      order: state.order,
    }
    const endpoint: AthenaQueryTraceEvent['endpoint'] = mergedOptions?.get
      ? `/rpc/${functionName}`
      : '/gateway/rpc'
    const sql = buildRpcDebugSql(payload)
    const debugAst = debugAstEnabled
      ? buildRpcDebugAst({
          functionName,
          args,
          selectedColumns: normalizedSelectedColumns,
          state,
          payload,
          endpoint,
        })
      : undefined
    return executeWithQueryTrace(
      tracer,
      {
        operation: 'rpc',
        endpoint,
        functionName,
        sql,
        payload,
        ast: debugAst,
        options: mergedOptions,
      },
      async () => {
        const response = await client.rpcGateway<SelectedRow[]>(payload, mergedOptions)
        return formatGatewayResult(response, { operation: 'rpc' })
      },
      callsite,
    )
  }

  const run = (
    columns?: AthenaSelectInput,
    options?: AthenaRpcCallOptions,
    callsite?: AthenaQueryTraceCallsite | null,
  ) => {
    const payloadColumns = columns ?? selectedColumns
    const payloadOptions = options ?? selectedOptions
    if (!promise) {
      promise = executeRpc<Row>(payloadColumns, payloadOptions, callsiteStore.resolve(callsite))
    }
    return promise
  }

  const builder = {} as RpcQueryBuilder<Row>
  const filterMethods = createRpcFilterMethods(state.filters, builder)

  Object.assign(builder, filterMethods, {
    select(columns?: AthenaSelectInput, options?: AthenaRpcCallOptions) {
      selectedColumns = columns
      selectedOptions = options ?? selectedOptions
      return run(columns, options, captureTraceCallsite(tracer))
    },
    async single<T = Row>(columns?: AthenaSelectInput, options?: AthenaRpcCallOptions) {
      const result = await run(columns, options, captureTraceCallsite(tracer))
      return toSingleResult(result) as AthenaResult<T | null>
    },
    maybeSingle<T = Row>(columns?: AthenaSelectInput, options?: AthenaRpcCallOptions) {
      return builder.single<T, AthenaSelectInput>(columns, options)
    },
    order(column: string, options?: RpcOrderOptions) {
      state.order = { column, ascending: options?.ascending ?? true }
      return builder
    },
    limit(count: number) {
      state.limit = count
      return builder
    },
    offset(count: number) {
      state.offset = count
      return builder
    },
    range(from: number, to: number) {
      state.offset = from
      state.limit = to - from + 1
      return builder
    },
    then<T1 = AthenaResult<Row[]>, T2 = never>(
      onfulfilled?: (v: AthenaResult<Row[]>) => T1 | PromiseLike<T1>,
      onrejected?: (reason: unknown) => T2 | PromiseLike<T2>,
    ) {
      return run(selectedColumns, selectedOptions).then(onfulfilled, onrejected)
    },
    catch<T = never>(onrejected?: (reason: unknown) => T | PromiseLike<T>) {
      return run(selectedColumns, selectedOptions).catch(onrejected)
    },
    finally(onfinally?: () => void) {
      return run(selectedColumns, selectedOptions).finally(onfinally)
    },
  })

  return builder
}

function createTableBuilder<
  Row,
  Insert = Partial<Row>,
  Update = Partial<Insert>,
  TContext = unknown,
>(
  tableName: string,
  client: ReturnType<typeof createAthenaGatewayClient>,
  formatGatewayResult: AthenaResultFormatter,
  tracer?: AthenaQueryTracer,
  behavior?: InternalClientBehaviorOptions,
): TableQueryBuilder<Row, Insert, Update, TContext> {
  const state: TableBuilderState = {
    conditions: [],
  }
  const debugAstEnabled = Boolean(behavior?.debugAst)

  const addCondition = (
    operator: AthenaConditionOperator,
    column?: string,
    value?: AthenaConditionValue | AthenaConditionArrayValue | string,
    hints?: ConditionCastHints,
  ) => {
    const condition: AthenaGatewayCondition = { operator }
    if (column) {
      condition.column = column
      if (operator === 'eq') {
        // include legacy gateway shape for compatibility
        condition.eq_column = column
      }
    }
    if (value !== undefined) {
      condition.value = value
      if (operator === 'eq') {
        condition.eq_value = value
      }
    }
    if (hints?.valueCast) {
      condition.value_cast = hints.valueCast
      if (operator === 'eq') {
        condition.eq_value_cast = hints.valueCast
      }
    }
    if (hints?.columnCast) {
      condition.column_cast = hints.columnCast
      if (operator === 'eq') {
        condition.eq_column_cast = hints.columnCast
      }
    }
    state.conditions.push(condition)
  }

  const snapshotState = (): TableBuilderState => ({
    conditions: state.conditions.map(condition => ({ ...condition })),
    limit: state.limit,
    offset: state.offset,
    order: state.order ? { ...state.order } : undefined,
    currentPage: state.currentPage,
    pageSize: state.pageSize,
    totalPages: state.totalPages,
  })

  const builder = {} as TableQueryBuilder<Row, Insert, Update, TContext>

  const filterMethods = createFilterMethods<TableQueryBuilder<Row, Insert, Update, TContext>, Row>(
    state,
    addCondition,
    builder,
  )

  const runSelect = async <T = Row>(
    columns: AthenaSelectInput = DEFAULT_COLUMNS,
    options?: AthenaGatewayCallOptions,
    executionState: TableBuilderState = snapshotState(),
    callsite?: AthenaQueryTraceCallsite | null,
    debugAstFactory?: SelectDebugAstFactory,
  ) => {
    const runtimeColumns = normalizeSelectColumnsInput(columns) ?? DEFAULT_COLUMNS
    const resolvedTableName = resolveTableNameForCall(tableName, options?.schema)
    const plan = createSelectTransportPlan({
      tableName: resolvedTableName,
      columns: runtimeColumns,
      state: executionState,
      options,
      buildTypedSelectQuery,
    })
    const debugAst = debugAstEnabled
      ? (debugAstFactory?.({
          tableName: resolvedTableName,
          columns: runtimeColumns,
          executionState,
          plan,
        }) ?? buildSelectDebugAst({
          tableName: resolvedTableName,
          columns: runtimeColumns,
          state: executionState,
          plan,
        }))
      : undefined

    if (plan.kind === 'query') {
      return executeRead(behavior, () =>
        executeWithQueryTrace(
          tracer,
          {
            operation: 'select',
            endpoint: '/gateway/query',
            table: resolvedTableName,
            sql: plan.query,
            payload: plan.payload,
            ast: debugAst,
            options,
          },
          async () => {
            const queryResponse = await client.queryGateway<T>(plan.payload, options)
            return formatGatewayResult(queryResponse, { table: resolvedTableName, operation: 'select' })
          },
          callsite,
        ),
      )
    }

    const sql = buildDebugSelectQuery({
      tableName: resolvedTableName,
      ...plan.debug,
    })
    return executeRead(behavior, () =>
      executeWithQueryTrace(
        tracer,
        {
          operation: 'select',
          endpoint: '/gateway/fetch',
          table: resolvedTableName,
          sql,
          payload: plan.payload,
          ast: debugAst,
          options,
        },
        async () => {
          const response = await client.fetchGateway<T>(plan.payload, options)
          return formatGatewayResult(response, { table: resolvedTableName, operation: 'select' })
        },
        callsite,
      ),
    )
  }

  const createSelectChain = <SelectedRow>(
    columns: AthenaSelectInput,
    options?: AthenaGatewayCallOptions,
    initialCallsite?: AthenaQueryTraceCallsite | null,
  ): SelectChain<Row, SelectedRow> => {
    const chain = {} as SelectChain<Row, SelectedRow>
    const callsiteStore = createTraceCallsiteStore(tracer, initialCallsite)
    const filterMethods = createFilterMethods<SelectChain<Row, SelectedRow>, Row>(
      state,
      addCondition,
      chain,
    )
    Object.assign(chain, filterMethods, {
      async single<T = SelectedRow>(cols?: AthenaSelectInput, opts?: AthenaGatewayCallOptions) {
        const r = await runSelect<T[]>(
          cols ?? columns,
          opts ?? options,
          snapshotState(),
          callsiteStore.resolve(captureTraceCallsite(tracer)),
        )
        return toSingleResult(r)
      },
      maybeSingle<T = SelectedRow>(cols?: AthenaSelectInput, opts?: AthenaGatewayCallOptions) {
        return chain.single<T, AthenaSelectInput>(cols, opts)
      },
      then<T1 = AthenaResult<SelectedRow[]>, T2 = never>(
        onfulfilled?: (v: AthenaResult<SelectedRow[]>) => T1 | PromiseLike<T1>,
        onrejected?: (reason: unknown) => T2 | PromiseLike<T2>,
      ) {
        return runSelect<SelectedRow[]>(
          columns,
          options,
          snapshotState(),
          callsiteStore.resolve(),
        ).then(onfulfilled, onrejected)
      },
      catch<T = never>(onrejected?: (reason: unknown) => T | PromiseLike<T>) {
        return runSelect<SelectedRow[]>(columns, options, snapshotState(), callsiteStore.resolve()).catch(
          onrejected,
        )
      },
      finally(onfinally?: () => void) {
        return runSelect<SelectedRow[]>(columns, options, snapshotState(), callsiteStore.resolve()).finally(
          onfinally,
        )
      },
    })
    return chain
  }

  Object.assign(builder, filterMethods, {
    reset() {
      state.conditions = []
      state.limit = undefined
      state.offset = undefined
      state.order = undefined
      state.currentPage = undefined
      state.pageSize = undefined
      state.totalPages = undefined
      return builder
    },
    select<T = Row>(columns: AthenaSelectInput = DEFAULT_COLUMNS, options?: AthenaGatewayCallOptions) {
      return createSelectChain<T>(columns, options, captureTraceCallsite(tracer))
    },
    async findMany<const TSelect extends AthenaSelectShape>(
      options: AthenaFindManyOptions<Row, TSelect> & {
        select: AthenaValidatedSelectShape<Row, TSelect>
      },
    ) {
      const columns = compileSelectShape(options.select)
      const baseState = snapshotState()
      const executionState = snapshotState()
      const callsite = captureTraceCallsite(tracer)
      const compiledWhere = compileWhere(options.where)
      if (compiledWhere?.length) {
        executionState.conditions.push(...compiledWhere)
      }
      if (options.orderBy !== undefined) {
        executionState.order = compileOrderBy<Row>(options.orderBy)
      }
      if (options.limit !== undefined) {
        executionState.limit = options.limit
      }
      if (
        behavior?.findManyAst &&
        canUseFindManyAstTransport(baseState) &&
        !selectShapeUsesRelationSchema(options.select) &&
        !findManyAstWhereRequiresLegacyTransport(options.where)
      ) {
        const resolvedTableName = resolveTableNameForCall(tableName, undefined)
        const payload: AthenaFindManyAstPayload<Row, TSelect> = {
          table_name: resolvedTableName,
          select: options.select,
        }
        if (options.where !== undefined) {
          payload.where = normalizeFindManyAstWhere(options.where)
        }
        const astOrder = toFindManyAstOrder<Row>(executionState.order)
        if (astOrder !== undefined) {
          payload.orderBy = astOrder
        }
        if (executionState.limit !== undefined) {
          payload.limit = executionState.limit
        }
        const sql = buildDebugSelectQuery({
          tableName: resolvedTableName,
          columns,
          conditions: executionState.conditions,
          limit: executionState.limit,
          order: executionState.order,
        })
        const debugAst = debugAstEnabled
          ? buildFindManyDirectDebugAst({
              tableName: resolvedTableName,
              options,
              compiledColumns: columns,
              baseState,
              executionState,
              payload,
            })
          : undefined
        return executeRead(behavior, () =>
          executeWithQueryTrace(
            tracer,
            {
              operation: 'select',
              endpoint: '/gateway/fetch',
              table: resolvedTableName,
              sql,
              payload,
              ast: debugAst,
            },
            async () => {
              const response = await client.fetchGateway<Array<AthenaFindManyResult<Row, TSelect, TContext>>>(
                payload,
              )
              return formatGatewayResult(response, { table: resolvedTableName, operation: 'select' })
            },
            callsite,
          ),
        )
      }
      return runSelect<Array<AthenaFindManyResult<Row, TSelect, TContext>>>(
        columns,
        undefined,
        executionState,
        callsite,
        debugAstEnabled
          ? ({ tableName: resolvedTableName, executionState: tracedState, plan }) =>
              buildFindManyCompiledDebugAst({
                tableName: resolvedTableName,
                options,
                compiledColumns: columns,
                baseState,
                executionState: tracedState,
                plan,
              })
          : undefined,
      )
    },
    insert(values: Insert | Insert[], options?: AthenaGatewayCallOptions) {
      const mutationCallsite = captureTraceCallsite(tracer)
      if (Array.isArray(values)) {
        const executeInsertMany = async (
          columns?: string | string[],
          selectOptions?: AthenaGatewayCallOptions,
          callsite?: AthenaQueryTraceCallsite | null,
        ) => {
          const mergedOptions = mergeOptions(options, selectOptions)
          const resolvedTableName = resolveTableNameForCall(tableName, mergedOptions?.schema)
          const payload: AthenaInsertPayload = {
            table_name: resolvedTableName,
            insert_body: asAthenaJsonObjectArray(values),
          }
          if (columns) payload.columns = columns
          if (mergedOptions?.count) payload.count = mergedOptions.count
          if (mergedOptions?.head) payload.head = mergedOptions.head
          if (mergedOptions?.defaultToNull !== undefined) {
            payload.default_to_null = mergedOptions.defaultToNull
          }
          const sql = buildInsertDebugSql(payload)
          const debugAst = debugAstEnabled ? buildInsertDebugAst(payload) : undefined
          return executeWithQueryTrace(
            tracer,
            {
              operation: 'insert',
              endpoint: '/gateway/insert',
              table: resolvedTableName,
              sql,
              payload,
              ast: debugAst,
              options: mergedOptions,
            },
            async () => {
              const response = await client.insertGateway<Row[]>(payload, mergedOptions)
              return formatGatewayResult(response, { table: resolvedTableName, operation: 'insert' })
            },
            callsite,
          )
        }
        return createMutationQuery<Row[]>(executeInsertMany, DEFAULT_COLUMNS, tracer, mutationCallsite)
      }
      const executeInsertOne = async (
        columns?: string | string[],
        selectOptions?: AthenaGatewayCallOptions,
        callsite?: AthenaQueryTraceCallsite | null,
      ) => {
        const mergedOptions = mergeOptions(options, selectOptions)
        const resolvedTableName = resolveTableNameForCall(tableName, mergedOptions?.schema)
        const payload: AthenaInsertPayload = {
          table_name: resolvedTableName,
          insert_body: asAthenaJsonObject(values),
        }
        if (columns) payload.columns = columns
        if (mergedOptions?.count) payload.count = mergedOptions.count
        if (mergedOptions?.head) payload.head = mergedOptions.head
        if (mergedOptions?.defaultToNull !== undefined) {
          payload.default_to_null = mergedOptions.defaultToNull
        }
        const sql = buildInsertDebugSql(payload)
        const debugAst = debugAstEnabled ? buildInsertDebugAst(payload) : undefined
        return executeWithQueryTrace(
          tracer,
          {
            operation: 'insert',
            endpoint: '/gateway/insert',
            table: resolvedTableName,
            sql,
            payload,
            ast: debugAst,
            options: mergedOptions,
          },
          async () => {
            const response = await client.insertGateway<Row>(payload, mergedOptions)
            return formatGatewayResult(response, { table: resolvedTableName, operation: 'insert' })
          },
          callsite,
        )
      }
      return createMutationQuery<Row>(executeInsertOne, DEFAULT_COLUMNS, tracer, mutationCallsite)
    },
    upsert(
      values: Insert | Insert[],
      options?: AthenaGatewayCallOptions & { updateBody?: Update; onConflict?: string | string[] },
    ) {
      const mutationCallsite = captureTraceCallsite(tracer)
      if (Array.isArray(values)) {
        const executeUpsertMany = async (
          columns?: string | string[],
          selectOptions?: AthenaGatewayCallOptions,
          callsite?: AthenaQueryTraceCallsite | null,
        ) => {
          const mergedOptions = mergeOptions(options, selectOptions)
          const resolvedTableName = resolveTableNameForCall(tableName, mergedOptions?.schema)
          const payload: AthenaInsertPayload = {
            table_name: resolvedTableName,
            insert_body: asAthenaJsonObjectArray(values),
            update_body: options?.updateBody ? asAthenaJsonObject(options.updateBody) : undefined,
          }
          if (columns) payload.columns = columns
          if (options?.onConflict) payload.on_conflict = options.onConflict
          if (mergedOptions?.count) payload.count = mergedOptions.count
          if (mergedOptions?.head) payload.head = mergedOptions.head
          if (mergedOptions?.defaultToNull !== undefined) {
            payload.default_to_null = mergedOptions.defaultToNull
          }
          const sql = buildInsertDebugSql(payload)
          const debugAst = debugAstEnabled ? buildUpsertDebugAst(payload) : undefined
          return executeWithQueryTrace(
            tracer,
            {
              operation: 'upsert',
              endpoint: '/gateway/insert',
              table: resolvedTableName,
              sql,
              payload,
              ast: debugAst,
              options: mergedOptions,
            },
            async () => {
              const response = await client.insertGateway<Row[]>(payload, mergedOptions)
              return formatGatewayResult(response, { table: resolvedTableName, operation: 'insert' })
            },
            callsite,
          )
        }
        return createMutationQuery<Row[]>(executeUpsertMany, DEFAULT_COLUMNS, tracer, mutationCallsite)
      }
      const executeUpsertOne = async (
        columns?: string | string[],
        selectOptions?: AthenaGatewayCallOptions,
        callsite?: AthenaQueryTraceCallsite | null,
      ) => {
        const mergedOptions = mergeOptions(options, selectOptions)
        const resolvedTableName = resolveTableNameForCall(tableName, mergedOptions?.schema)
        const payload: AthenaInsertPayload = {
          table_name: resolvedTableName,
          insert_body: asAthenaJsonObject(values),
          update_body: options?.updateBody ? asAthenaJsonObject(options.updateBody) : undefined,
        }
        if (columns) payload.columns = columns
        if (options?.onConflict) payload.on_conflict = options.onConflict
        if (mergedOptions?.count) payload.count = mergedOptions.count
        if (mergedOptions?.head) payload.head = mergedOptions.head
        if (mergedOptions?.defaultToNull !== undefined) {
          payload.default_to_null = mergedOptions.defaultToNull
        }
        const sql = buildInsertDebugSql(payload)
        const debugAst = debugAstEnabled ? buildUpsertDebugAst(payload) : undefined
        return executeWithQueryTrace(
          tracer,
          {
            operation: 'upsert',
            endpoint: '/gateway/insert',
            table: resolvedTableName,
            sql,
            payload,
            ast: debugAst,
            options: mergedOptions,
          },
          async () => {
            const response = await client.insertGateway<Row>(payload, mergedOptions)
            return formatGatewayResult(response, { table: resolvedTableName, operation: 'insert' })
          },
          callsite,
        )
      }
      return createMutationQuery<Row>(executeUpsertOne, DEFAULT_COLUMNS, tracer, mutationCallsite)
    },
    update(values: Update, options?: AthenaGatewayCallOptions) {
      const mutationCallsite = captureTraceCallsite(tracer)
      const executeUpdate = async (
        columns?: string | string[],
        selectOptions?: AthenaGatewayCallOptions,
        callsite?: AthenaQueryTraceCallsite | null,
      ) => {
        const executionState = snapshotState()
        const filters = executionState.conditions.length ? [...executionState.conditions] : undefined
        const mergedOptions = mergeOptions(options, selectOptions)
        const resolvedTableName = resolveTableNameForCall(tableName, mergedOptions?.schema)
        const payload: AthenaUpdatePayload = {
          table_name: resolvedTableName,
          update_body: asAthenaJsonObject(values),
          conditions: filters,
          strip_nulls: mergedOptions?.stripNulls ?? true,
        }
        if (executionState.order) payload.sort_by = executionState.order
        if (executionState.currentPage !== undefined) payload.current_page = executionState.currentPage
        if (executionState.pageSize !== undefined) payload.page_size = executionState.pageSize
        if (executionState.totalPages !== undefined) payload.total_pages = executionState.totalPages
        if (columns) payload.columns = columns
        const sql = buildUpdateDebugSql(payload)
        const debugAst = debugAstEnabled
          ? buildUpdateDebugAst({
              state: executionState,
              payload,
            })
          : undefined
        return executeWithQueryTrace(
          tracer,
          {
            operation: 'update',
            endpoint: '/gateway/update',
            table: resolvedTableName,
            sql,
            payload,
            ast: debugAst,
            options: mergedOptions,
          },
          async () => {
            const response = await client.updateGateway<Row[]>(payload, mergedOptions)
            return formatGatewayResult(response, { table: resolvedTableName, operation: 'update' })
          },
          callsite,
        )
      }
      const mutation = createMutationQuery<Row[]>(executeUpdate, null, tracer, mutationCallsite)
      const updateChain = {} as UpdateChain<Row>
      const filterMethods = createFilterMethods<UpdateChain<Row>, Row>(state, addCondition, updateChain)
      Object.assign(updateChain, filterMethods, mutation)
      return updateChain
    },
    delete(options?: AthenaGatewayCallOptions & { resourceId?: string }) {
      const filters = state.conditions.length ? [...state.conditions] : undefined
      const resourceId = options?.resourceId ?? getResourceId(state)
      if (!resourceId && !filters?.length) {
        throw new Error('delete requires a resource_id either via eq("resource_id", ...) or options.resourceId')
      }
      const mutationCallsite = captureTraceCallsite(tracer)
      const executeDelete = async (
        columns?: string | string[],
        selectOptions?: AthenaGatewayCallOptions,
        callsite?: AthenaQueryTraceCallsite | null,
      ) => {
        const executionState = snapshotState()
        const debugState: TableBuilderState = {
          ...executionState,
          conditions: filters ? filters.map(condition => ({ ...condition })) : [],
        }
        const mergedOptions = mergeOptions(options, selectOptions)
        const resolvedTableName = resolveTableNameForCall(tableName, mergedOptions?.schema)
        const payload: AthenaDeletePayload = {
          table_name: resolvedTableName,
          resource_id: resourceId,
          conditions: filters,
        }
        if (executionState.order) payload.sort_by = executionState.order
        if (executionState.currentPage !== undefined) payload.current_page = executionState.currentPage
        if (executionState.pageSize !== undefined) payload.page_size = executionState.pageSize
        if (executionState.totalPages !== undefined) payload.total_pages = executionState.totalPages
        if (columns) payload.columns = columns
        const sql = buildDeleteDebugSql(payload)
        const debugAst = debugAstEnabled
          ? buildDeleteDebugAst({
              state: debugState,
              payload,
            })
          : undefined
        return executeWithQueryTrace(
          tracer,
          {
            operation: 'delete',
            endpoint: '/gateway/delete',
            table: resolvedTableName,
            sql,
            payload,
            ast: debugAst,
            options: mergedOptions,
          },
          async () => {
            const response = await client.deleteGateway<Row | null>(payload, mergedOptions)
            return formatGatewayResult(response, { table: resolvedTableName, operation: 'delete' })
          },
          callsite,
        )
      }
      return createMutationQuery<Row | null>(executeDelete, null, tracer, mutationCallsite)
    },
    async single<T = Row>(columns?: AthenaSelectInput, options?: AthenaGatewayCallOptions) {
      const response = await runSelect<T[]>(
        columns ?? DEFAULT_COLUMNS,
        options,
        snapshotState(),
        captureTraceCallsite(tracer),
      )
      return toSingleResult(response)
    },
    async maybeSingle<T = Row>(columns?: AthenaSelectInput, options?: AthenaGatewayCallOptions) {
      return builder.single<T, AthenaSelectInput>(columns, options)
    },
  })

  return builder
}

function createQueryBuilder(
  client: ReturnType<typeof createAthenaGatewayClient>,
  formatGatewayResult: AthenaResultFormatter,
  behavior?: InternalClientBehaviorOptions,
  tracer?: AthenaQueryTracer,
) {
  const debugAstEnabled = Boolean(behavior?.debugAst)
  return async function query<Row = unknown>(
    query: string,
    options?: AthenaGatewayCallOptions,
  ): Promise<AthenaResult<Row[]>> {
    const normalizedQuery = query.trim()
    if (!normalizedQuery) {
      throw new Error('query requires a non-empty string')
    }
    const payload = { query: normalizedQuery }
    const callsite = captureTraceCallsite(tracer)
    return executeRead(behavior, () =>
      executeWithQueryTrace(
        tracer,
        {
          operation: 'query',
          endpoint: '/gateway/query',
          sql: normalizedQuery,
          payload,
          ast: debugAstEnabled ? buildRawQueryDebugAst(normalizedQuery) : undefined,
          options,
        },
        async () => {
          const response = await client.queryGateway<Row[]>(payload, options)
          return formatGatewayResult(response, { operation: 'query' })
        },
        callsite,
      ),
    )
  }
}

export interface InternalAthenaClient<TModels = never> {
  from<TModel extends AthenaModelTarget>(
    model: TModel,
  ): TableQueryBuilder<RowOf<TModel>, InsertOf<TModel>, UpdateOf<TModel>>
  from<TTableName extends AthenaClientTableName<TModels>>(
    table: TTableName,
    options?: AthenaFromOptions,
  ): ClientTableQueryBuilder<TModels, TTableName>
  from<
    Row = AthenaRowShape,
    Insert = Partial<Row>,
    Update = Partial<Insert>,
  >(
    table: UntypedTableName<TModels>,
    options?: AthenaFromOptions,
  ): TableQueryBuilder<Row, Insert, Update>
  db: AthenaDbModule<TModels>
  auth: AthenaAuthBindings
  chat: AthenaChatModule
  storage: AthenaStorageModule
  rpc<Row = unknown, Args extends AthenaJsonObject = AthenaJsonObject>(
    fn: string,
    args?: Args,
    options?: AthenaRpcCallOptions,
  ): RpcQueryBuilder<Row>
  query<Row = unknown>(query: string, options?: AthenaGatewayCallOptions): Promise<AthenaResult<Row[]>>
  request<T = unknown>(options: AthenaRequestOptions): Promise<AthenaRequestResponse<T>>
  verifyConnection(options?: AthenaGatewayConnectionOptions): Promise<AthenaGatewayConnectionResult>
}

export interface InternalClientChatOptions {
  wsUrl?: string | null | undefined
  webSocketFactory?: AthenaChatWebSocketFactory | null | undefined
}

export type InternalClientAuthOptions = Omit<
  AthenaAuthClientConfig,
  'baseUrl' | 'apiKey' | 'bearerToken' | 'cookie' | 'sessionToken'
>

export interface InternalClientConfig<TModels extends AthenaClientModelsInput | never = never> {
  baseUrl: string
  apiKey: string
  client?: string | null | undefined
  /** Direct PostgreSQL URI forwarded as `x-pg-uri` on gateway requests. */
  pgUri?: string | null | undefined
  jdbcUrl?: string | null | undefined
  backend?: BackendConfig
  headers?: Record<string, string>
  auth?: InternalClientAuthOptions
  authUrl?: string
  chat?: InternalClientChatOptions
  chatUrl?: string
  chatWsUrl?: string
  storageUrl?: string
  storage?: AthenaStorageClientConfig
  behavior?: InternalClientBehaviorOptions
  models?: TModels
}

export interface InternalClientRequestContext {
  headers?: Record<string, string>
  userId?: string | null
  organizationId?: string | null
  bearerToken?: string | null
  cookie?: string | null
  sessionToken?: string | null
  forceNoCache?: boolean
}

export type InternalClientContextResolver = () =>
  | InternalClientRequestContext
  | undefined
  | Promise<InternalClientRequestContext | undefined>

export interface InternalAthenaClientCore<
  TModels extends AthenaClientModelsInput | never = never,
> {
  readonly config: InternalClientConfig<TModels>
  readonly gatewayTransport: AthenaGatewayClient
  readonly formatGatewayResult: AthenaResultFormatter
  readonly queryTracer: AthenaQueryTracer | undefined
  readonly normalizedAuthConfig: AthenaAuthClientConfig | undefined
}

function buildContextHeaders(
  context: InternalClientRequestContext,
): Record<string, string> | undefined {
  const headers: Record<string, string> = {}
  if (context.userId != null) {
    headers['X-User-Id'] = context.userId
  }
  if (context.organizationId != null) {
    headers['X-Organization-Id'] = context.organizationId
  }
  Object.assign(headers, context.headers)
  return Object.keys(headers).length > 0 ? headers : undefined
}

function normalizeAuthClientConfig(
  auth: InternalClientAuthOptions | undefined,
  defaultBaseUrl?: string,
): AthenaAuthClientConfig | undefined {
  if (!auth && defaultBaseUrl === undefined) {
    return undefined
  }

  const normalized: AthenaAuthClientConfig = {
    ...(auth ?? {}),
  }
  const resolvedBaseUrl = defaultBaseUrl
    ? normalizeAthenaGatewayBaseUrl(defaultBaseUrl, { label: 'Athena auth base URL' })
    : undefined

  if (resolvedBaseUrl !== undefined) {
    normalized.baseUrl = resolvedBaseUrl
  }
  return normalized
}

export function createInternalClientCore<
  TModels extends AthenaClientModelsInput | never = never,
>(
  config: InternalClientConfig<TModels>,
): InternalAthenaClientCore<TModels> {
  const normalizedAuthConfig = normalizeAuthClientConfig(config.auth, config.authUrl)
  const gatewayTransport = createAthenaGatewayClient({
    baseUrl: config.baseUrl,
    apiKey: config.apiKey,
    client: config.client ?? undefined,
    pgUri: config.pgUri,
    jdbcUrl: config.jdbcUrl,
    backend: config.backend,
    headers: config.headers,
  })
  return Object.freeze({
    config,
    gatewayTransport,
    formatGatewayResult: createResultFormatter(),
    queryTracer: createQueryTracer(config.behavior),
    normalizedAuthConfig,
  })
}

export function createInternalClientView<
  TModels extends AthenaClientModelsInput | never = never,
>(
  core: InternalAthenaClientCore<TModels>,
  resolveContext: InternalClientContextResolver,
): InternalAthenaClient<TModels> {
  const { config, formatGatewayResult, normalizedAuthConfig, queryTracer } = core
  const gateway = createAthenaGatewayClientView(core.gatewayTransport, async () => {
    const context = await resolveContext()
    return context
      ? {
          headers: context.headers,
          userId: context.userId,
          organizationId: context.organizationId,
          bearerToken: context.bearerToken,
          cookie: context.cookie,
          sessionToken: context.sessionToken,
          forceNoCache: context.forceNoCache,
        }
      : undefined
  })
  const auth = createAuthModule({
    ...(normalizedAuthConfig ?? {}),
  }, {
    async resolveCallOptions() {
      const context = await resolveContext()
      return context
        ? {
            headers: buildContextHeaders(context),
            bearerToken: context.bearerToken ?? undefined,
            cookie: context.cookie ?? undefined,
            sessionToken: context.sessionToken ?? undefined,
            forceNoCache: context.forceNoCache,
          }
        : undefined
    },
  })
  function from<TModel extends AthenaModelTarget>(
    model: TModel,
  ): TableQueryBuilder<RowOf<TModel>, InsertOf<TModel>, UpdateOf<TModel>>
  function from<TTableName extends AthenaClientTableName<TModels>>(
    table: TTableName,
    options?: AthenaFromOptions,
  ): ClientTableQueryBuilder<TModels, TTableName>
  function from<
    Row = AthenaRowShape,
    Insert = Partial<Row>,
    Update = Partial<Insert>,
  >(
    table: UntypedTableName<TModels>,
    options?: AthenaFromOptions,
  ): TableQueryBuilder<Row, Insert, Update>
  function from<
    Row = AthenaRowShape,
    Insert = Partial<Row>,
    Update = Partial<Insert>,
  >(
    tableOrModel: string | AthenaModelTarget<Row, Insert, Update>,
    options?: AthenaFromOptions,
  ): TableQueryBuilder<Row, Insert, Update> {
    if (isAthenaModelTarget(tableOrModel)) {
      if (options?.schema !== undefined) {
        throw new Error(
          'from(model) does not accept a schema override because the model already defines its target.',
        )
      }
      return createTableBuilder<Row, Insert, Update>(
        resolveAthenaModelTargetTableName(tableOrModel),
        gateway,
        formatGatewayResult,
        queryTracer,
        config.behavior,
      )
    }

    const resolvedTableName = resolveTableNameForCall(tableOrModel as string, options?.schema)
    return createTableBuilder<Row, Insert, Update>(
      resolvedTableName,
      gateway,
      formatGatewayResult,
      queryTracer,
      config.behavior,
    )
  }
  const rpc: InternalAthenaClient<TModels>['rpc'] = <Row = unknown, Args extends AthenaJsonObject = AthenaJsonObject>(
    fn: string,
    args?: Args,
    options?: AthenaRpcCallOptions,
  ) => {
    const normalizedFn = fn.trim()
    if (!normalizedFn) {
      throw new Error('rpc requires a function name')
    }
    return createRpcBuilder<Row>(
      normalizedFn,
      args as AthenaJsonObject | undefined,
      options,
      gateway,
      formatGatewayResult,
      queryTracer,
      captureTraceCallsite(queryTracer),
      Boolean(config.behavior?.debugAst),
    )
  }
  const query = createQueryBuilder(
    gateway,
    formatGatewayResult,
    config.behavior,
    queryTracer,
  ) as InternalAthenaClient<TModels>['query']
  const untypedDbFrom = from as unknown as AthenaDbModule['from']
  const db = {
    from,
    select<Row = AthenaRowShape>(
      table: string,
      first?: AthenaGatewayCallOptions | AthenaSelectInput,
      second?: AthenaGatewayCallOptions,
    ) {
      if (first && typeof first === 'object' && !Array.isArray(first)) {
        return untypedDbFrom<Row>(table).select(undefined, first as AthenaGatewayCallOptions)
      }
      return untypedDbFrom<Row>(table).select(first, second)
    },
    insert<Row = AthenaRowShape, Insert = Partial<Row>>(
      table: string,
      values: Insert | Insert[],
      options?: AthenaGatewayCallOptions,
    ) {
      return Array.isArray(values)
        ? untypedDbFrom<Row, Insert, Partial<Insert>>(table).insert(values, options)
        : untypedDbFrom<Row, Insert, Partial<Insert>>(table).insert(values, options)
    },
    upsert<Row = AthenaRowShape, Insert = Partial<Row>, Update = Partial<Insert>>(
      table: string,
      values: Insert | Insert[],
      options?: AthenaGatewayCallOptions & {
        updateBody?: Update
        onConflict?: string | string[]
      },
    ) {
      return Array.isArray(values)
        ? untypedDbFrom<Row, Insert, Update>(table).upsert(values, options)
        : untypedDbFrom<Row, Insert, Update>(table).upsert(values, options)
    },
    update<Row = AthenaRowShape, Insert = Partial<Row>, Update = Partial<Insert>>(
      table: string,
      values: Update,
      options?: AthenaGatewayCallOptions,
    ) {
      return untypedDbFrom<Row, Insert, Update>(table).update(values, options)
    },
    delete<Row = AthenaRowShape>(
      table: string,
      options?: AthenaGatewayCallOptions & { resourceId?: string },
    ) {
      return untypedDbFrom<Row>(table).delete(options)
    },
    rpc,
    query,
  } as AthenaDbModule<TModels>
  const chat = createChatModule({
    baseUrl: config.chatUrl,
    apiKey: config.apiKey,
    client: config.client ?? undefined,
    headers: config.headers,
    wsUrl: config.chatWsUrl,
    webSocketFactory: config.chat?.webSocketFactory ?? undefined,
  }, {
    async resolveCallOptions() {
      const context = await resolveContext()
      return context
        ? {
            headers: buildContextHeaders(context),
            bearerToken: context.bearerToken,
            cookie: context.cookie,
            sessionToken: context.sessionToken,
            forceNoCache: context.forceNoCache,
          }
        : undefined
    },
  })
  const request = createAthenaRequest(config, resolveContext)
  const storage = createStorageModule(gateway, {
    ...config.storage,
    ...(config.storageUrl
      ? {
          baseUrl: config.storageUrl,
          stripBasePath: true,
        }
      : {}),
  })

  return {
    from,
    db,
    rpc,
    query,
    request,
    verifyConnection: gateway.verifyConnection,
    auth: auth.auth,
    chat,
    storage,
  } as InternalAthenaClient<TModels>
}
